// Recursive fund / rice cascade — single source of truth shared between
// Fund Management UI, Monthly MDCF auto-fill, and any other surface that
// must show class-wise opening/closing across multiple months.
//
// The closing of month N becomes the opening of month N+1. We walk back
// through every saved fund record (bounded depth 24 to avoid pathological
// loops) and re-derive each month's opening from the chain. The *first*
// saved month's stored opening is the chain anchor.
//
// Returns a Promise resolving to:
//   { fund: { balVatika, primary, upperPrimary, highSchool },
//     rice: { balVatika, primary, upperPrimary, highSchool } }
// or `null` when there is no record for (year, month).

import { fundApi } from "./setupApi";
import { consumedForMonth as NEW_consumedForMonth, ledgerConsumed } from "./fundCascadeNew";

const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];

const empty = () => ({ balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 });

export async function consumedForMonth(schoolId, year, month) {
  return NEW_consumedForMonth(schoolId, year, month);
}

// Recursively cascade. Pass a shared `cache` Map across many calls in a single
// render pass so we don't re-fetch + re-compute the same months repeatedly.
export async function cascadeClosing(schoolId, year, month, cache = new Map(), depth = 0) {
  const key = `${year}-${month}`;
  if (cache.has(key)) return cache.get(key);
  if (depth > 24) { cache.set(key, null); return null; }
  const cur = await fundApi.get(schoolId, year, month).catch(() => null);
  if (!cur) { cache.set(key, null); return null; }
  const py = month === 1 ? year - 1 : year;
  const pm = month === 1 ? 12 : month - 1;
  const prev = await cascadeClosing(schoolId, py, pm, cache, depth + 1);
  const opening = prev
    ? { fund: { ...prev.fund }, rice: { ...prev.rice } }
    : {
        fund: { ...empty(), ...(cur.openingFund || {}) },
        rice: { ...empty(), ...(cur.openingRice || {}) },
      };
  const cons = await consumedForMonth(schoolId, year, month);
  const closing = { fund: empty(), rice: empty() };
  CLASS_KEYS.forEach((k) => {
    closing.fund[k] = Number(((opening.fund[k] || 0) + Number(cur.receivedFund?.[k] || 0) - (cons.cost[k] || 0)).toFixed(2));
    closing.rice[k] = Number(((opening.rice[k] || 0) + Number(cur.receivedRice?.[k] || 0) - (cons.rice[k] || 0)).toFixed(3));
  });
  cache.set(key, closing);
  return closing;
}

// Compute opening (cascaded) for (year, month). The opening of month N is the
// cascaded closing of month N-1; if no record exists for N-1, fall back to
// (year, month)'s own stored opening as the chain anchor (which is what
// the user originally entered). Returns null if (year, month) itself has no record.
export async function cascadeOpening(schoolId, year, month, cache = new Map()) {
  const py = month === 1 ? year - 1 : year;
  const pm = month === 1 ? 12 : month - 1;
  const prev = await cascadeClosing(schoolId, py, pm, cache);
  if (prev) return { fund: { ...prev.fund }, rice: { ...prev.rice } };
  // Anchor: this month's stored opening
  const cur = await fundApi.get(schoolId, year, month).catch(() => null);
  if (!cur) return null;
  return {
    fund: { ...empty(), ...(cur.openingFund || {}) },
    rice: { ...empty(), ...(cur.openingRice || {}) },
  };
}

// Build a full per-month cascaded ledger for an array of saved fund balance
// records (must be sorted by [year, month] ascending). Returns a map keyed by
// `${year}-${month}` to { opening: {fund,rice}, received: {fund,rice}, consumed: {fund,rice}, closing: {fund,rice} }.
// Used by the Fund Management history table so every visible row shows the
// cascaded values, not the raw stored ones.
export async function buildFullLedger(schoolId, balances) {
  const cache = new Map();
  const ledger = new Map();
  for (const b of balances || []) {
    const closing = await cascadeClosing(schoolId, b.year, b.month, cache);
    if (!closing) continue;
    // Opening = cascaded closing of (b.year, b.month - 1); fall back to b's stored opening.
    const py = b.month === 1 ? b.year - 1 : b.year;
    const pm = b.month === 1 ? 12 : b.month - 1;
    const prev = await cascadeClosing(schoolId, py, pm, cache);
    const opening = prev
      ? { fund: { ...prev.fund }, rice: { ...prev.rice } }
      : {
          fund: { ...empty(), ...(b.openingFund || {}) },
          rice: { ...empty(), ...(b.openingRice || {}) },
        };
    const consumed = await consumedForMonth(schoolId, b.year, b.month);
    const received = {
      fund: { ...empty(), ...(b.receivedFund || {}) },
      rice: { ...empty(), ...(b.receivedRice || {}) },
    };
    ledger.set(`${b.year}-${b.month}`, { opening, received, consumed: ledgerConsumed(consumed), closing });
  }
  return ledger;
}
